import type { BadgeItem } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { openModal } from "risa-oasis-ui_v2";
import {
  bigquerySearchLike,
  BIGQUERY_SEARCH_LIKE_DATASET_NAME,
  BIGQUERY_SEARCH_LIKE_TABLE_NAME,
} from "../../../../api/searchLike";
import { ModalId } from "../../../../enums/modalId";
import type { CmmInputUniqueValueItem } from "../../../../pages/configurations/pages/validationFunnel/pages/conditionList/cmmInputUniqueValuesTypes";
import { closeModal } from "../../../../redux/slice/modalSliceNew";
import { AppDispatch } from "../../../../redux/store/store";

const MIN_SEARCH_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_LIMIT = 10;
const CREATED_AT_CUTOFF_MONTHS = 3;

const truncateValue = (val: string | number | boolean, maxLen = 60) => {
  const s = String(val);
  return s.length > maxLen ? `${s.slice(0, maxLen)}…` : s;
};

interface UseConditionListTopValuesParams {
  onClose: () => void;
  onSave: (
    columnName: string,
    newTopValues: CmmInputUniqueValueItem[],
  ) => Promise<void>;
  columnName: string;
  topValues: CmmInputUniqueValueItem[];
}

export function useConditionListTopValues({
  onClose,
  onSave,
  columnName,
  topValues,
}: UseConditionListTopValuesParams) {
  const dispatch = useDispatch<AppDispatch>();
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [options, setOptions] = useState<{ label: string; value: string }[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const fetchOptions = useCallback(
    async (query: string, signal: AbortSignal) => {
      if (query.trim().length < MIN_SEARCH_LENGTH) {
        setOptions([]);
        setSearchError(null);
        return;
      }
      setLoading(true);
      setSearchError(null);
      try {
        const results = await bigquerySearchLike(
          {
            distinct: true,
            select_column: columnName,
            filter_column: columnName,
            query: query.trim(),
            limit: SEARCH_LIMIT,
            created_at_cutoff_months: CREATED_AT_CUTOFF_MONTHS,
            dataset_name: BIGQUERY_SEARCH_LIKE_DATASET_NAME,
            table_name: BIGQUERY_SEARCH_LIKE_TABLE_NAME,
          },
          signal,
        );
        const nextOptions = (results ?? []).map((text) => ({
          label: text,
          value: text,
        }));
        setOptions(nextOptions);
      } catch (err) {
        if ((err as Error).name === "AbortError") return;
        setSearchError((err as Error).message ?? "Search failed");
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [columnName],
  );

  const onSearch = useCallback(
    (query: string) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      if (abortRef.current) {
        abortRef.current.abort();
      }
      if (query.trim().length < MIN_SEARCH_LENGTH) {
        setOptions([]);
        setSearchError(null);
        return;
      }
      debounceRef.current = setTimeout(() => {
        abortRef.current = new AbortController();
        fetchOptions(query.trim(), abortRef.current.signal);
      }, SEARCH_DEBOUNCE_MS);
    },
    [fetchOptions],
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const handleClose = useCallback(() => {
    dispatch(closeModal());
    onClose();
  }, [dispatch, onClose]);

  const getMergedTopValues = useCallback((): CmmInputUniqueValueItem[] => {
    const existing = [...(topValues ?? [])];
    const existingValueSet = new Set(existing.map((t) => String(t.value)));
    for (const v of selectedValues) {
      if (!existingValueSet.has(v)) {
        existing.push({ value: v, count: 1 });
        existingValueSet.add(v);
      }
    }
    return existing;
  }, [topValues, selectedValues]);

  const handleSave = useCallback(async () => {
    setSaveError(null);
    setSaving(true);
    try {
      const newTopValues = getMergedTopValues();
      await onSave(columnName, newTopValues);
      handleClose();
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : "Failed to save top values",
      );
    } finally {
      setSaving(false);
    }
  }, [columnName, getMergedTopValues, onSave, handleClose]);

  const handleChange = useCallback((value: string[]) => {
    setSelectedValues(value);
  }, []);

  const badges: BadgeItem[] = useMemo(
    () =>
      (topValues ?? []).map((item) => ({
        text: `${truncateValue(item.value)} (${item.count})`,
      })),
    [topValues],
  );

  useEffect(() => {
    if (columnName) {
      openModal(ModalId.CONDITION_LIST_TOP_VALUES_MODAL);
    }
  }, [columnName]);

  const removeSelectedValue = useCallback((val: string) => {
    setSelectedValues((prev) => prev.filter((v) => v !== val));
  }, []);

  return {
    selectedValues,
    saving,
    saveError,
    options,
    loading,
    searchError,
    badges,
    handleClose,
    handleSave,
    handleChange,
    onSearch,
    removeSelectedValue,
    MIN_SEARCH_LENGTH,
  };
}
